// ExpiringTokensWebSocketInitializer.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FeedActionContext } from './FeedState';
import { useStateSelector } from '../global/dataSelectors';
import { useDispatcher } from '../global/hooks';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { useExpiringTokensFeedStatus, useExpiringTokensWebSocketCloseReason } from './FeedSelectors';
import { Websockets } from '../../constants';
import _ from 'lodash';
import { expiringTokensWebSocketService } from './ExpiringTokensWebSocketService';
import { useFeedActions } from './useFeedActions';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const ExpiringTokensWebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.expiringTokensFeed;
  const globalDispatch = dispatchers.dispatch;
  const expiringTokensStatusFeedStatus = useExpiringTokensFeedStatus();
  const closeReason = useExpiringTokensWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);
  const apiToken = useStateSelector(state => state.auth.apiToken);
  const { subscribeExpiringTokens, unsubscribeExpiringTokens } = useFeedActions(apiToken);

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      expiringTokensWebSocketService.closeConnection();
      unsubscribeExpiringTokens();
    };
  }, []);

  useEffect(() => {
    if (expiringTokensStatusFeedStatus && expiringTokensStatusFeedStatus === 'open') {
      subscribeExpiringTokens();
    }
  }, [expiringTokensStatusFeedStatus]);

  useEffect(() => {
    if (shouldReconnect && apiToken) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (expiringTokensWebSocketService.shouldTryReconnect()) {
          expiringTokensWebSocketService
            .connect(dispatch, dispatcher, globalDispatch, apiToken)
            .then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, apiToken]);

  useEffect(() => {
    if (expiringTokensStatusFeedStatus === 'closed') {
      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `Expiring Tokens Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Expiring Tokens is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [expiringTokensStatusFeedStatus, closeReason, canConnect]);

  useEffect(() => {
    if (canConnect && apiToken) {
      console.log('initializing Expiring Tokens websocket (this should only happen once!)');
      expiringTokensWebSocketService.connect(dispatch, dispatcher, globalDispatch, apiToken);
    } else if (!canConnect && !expiringTokensWebSocketService.shouldTryReconnect()) {
      console.log('disconnecting Expiring Tokens websocket because user can no longer connect');
      expiringTokensWebSocketService.closeConnection();
    } else {
      console.log('doing nothing');
    }
  }, [dispatcher, canConnect, apiToken]);

  return null; // This component does not render anything
};

export default ExpiringTokensWebSocketInitializer;
